/**
 * Venue naming for event emails.
 *
 * The Range Night email templates hardcode "Range Night at {{partner_name}}", where partner_name
 * is the event's Location Name. Event titles are written inconsistently ("X", "X Range Night",
 * "X - Range Night", "Range Night - X"), so anything built from them must not double the label.
 *
 * Mirrors stripRangeNightLabel in supabase/functions/_shared/eibInvitationContext.ts — keep the
 * two rules identical, because the subject line (built here) and the email body (built there)
 * should name the venue the same way.
 */

const RANGE_NIGHT_LEAD = /^\s*range\s+night\b\s*(?:at\b|[-–—:;,|])?\s*/i;
const RANGE_NIGHT_SEP_TAIL = /\s*[-–—:;,|]+\s*range\s+night\s*$/i;
const RANGE_NIGHT_DOUBLED_TAIL = /(\brange)\s+range\s+night\s*$/i;

/**
 * Remove a "Range Night" label only where it is unambiguously ours: leading, trailing after a
 * separator, or a doubled "Range Range Night". A bare trailing " Range Night" is kept — in
 * "Elite Shooting Range Night" the word "Range" belongs to the venue.
 */
export function stripRangeNightLabel(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .replace(RANGE_NIGHT_SEP_TAIL, "")
    .replace(RANGE_NIGHT_DOUBLED_TAIL, "$1")
    .replace(RANGE_NIGHT_LEAD, "")
    .trim();
}

/**
 * Default invitation subject. For a Range Night it names the event type and the venue
 * ("You're invited: Range Night at Marksman Indoor Range") whatever the title looks like.
 * Other event types keep the title, which is what their emails show.
 */
export function defaultInvitationSubject(event: {
  title?: string | null;
  type?: string | null;
  location_name?: string | null;
}): string {
  const title = (event.title ?? "").trim();
  if (event.type === "range_night") {
    const venue =
      stripRangeNightLabel(event.location_name) || stripRangeNightLabel(title);
    if (venue) {
      return `You're invited: Range Night at ${venue}`;
    }
  }
  return `You're invited: ${title}`;
}
